import React, {useEffect} from 'react';

import {Heading} from '@albert-io/atomic';
import appActions from 'client/AppActions';
import {callAction} from 'client/framework';

import {Sidemenu} from '../Sidemenu/Sidemenu';

const footerActions = appActions as Record<'SET_FOOTER_DISPLAY', symbol>;

interface Props {
  actions?: React.ReactNode;
  children: React.ReactNode;
  /** Replaces the default title/actions row entirely (e.g. the colored class banner). */
  header?: React.ReactNode;
  pathname: string;
  title?: string;
}

/**
 * Shared chrome for teacher dashboard v2 pages: persistent sidemenu plus an
 * optional page header (title + actions).
 */
export const TeacherDashboardLayout = ({actions, children, header, pathname, title}: Props) => {
  useEffect(() => {
    callAction(footerActions.SET_FOOTER_DISPLAY, false);

    return () => {
      callAction(footerActions.SET_FOOTER_DISPLAY, true);
    };
  }, []);

  const getMainContent = () => {
    if (header) {
      return (
        <main className='min-w-0 flex grow flex-col overflow-y-auto background-primary'>
          {header}
          <div className='grow px-x4 pb-x4'>{children}</div>
        </main>
      );
    }
    if (title || actions) {
      return (
        <main className='min-w-0 flex grow flex-col overflow-y-auto background-primary'>
          <header className='flex min-h-[100px] shrink-0 items-center gap-x1 p-x4'>
            {title ? (
              <Heading as='h1' className='m-x0 grow truncate leading-none' size='m'>
                {title}
              </Heading>
            ) : null}
            {actions ? <div className='flex shrink-0 items-center gap-x1'>{actions}</div> : null}
          </header>
          <div className='grow px-x4 pb-x4'>{children}</div>
        </main>
      );
    }
    return (
      <main className='min-w-0 grow overflow-y-auto background-primary'>
        <div className='p-x4'>{children}</div>
      </main>
    );
  };

  return (
    <div className='-mt-[60px] flex h-[calc(100vh-60px)] background-subtle'>
      <Sidemenu pathname={pathname} />
      {getMainContent()}
    </div>
  );
};
